/*
 * ST 2026-10-10 (Wrike 4582098438): delivery choice in the cart drawer. Markup: snippets/st-cart-delivery-choice.liquid.
 * Round 3 (Ali 11.10.2026): Wunschort / Aufbau are service products per device.
 * Round 6 (Simon's import contract, 11.10.2026): a choice sets the device line property `_service` (2mh | aufbau |
 *   express, removed for Standard) through /cart/change.js, adds the service variant with `_service_for` = device SKU
 *   (Shopify merges it into an existing line for the same SKU, so the quantity becomes the sum) and lowers the previous
 *   service line by this device's units in the /cart/update.js call that re-renders the drawer.
 * Round 5 (Ali 11.10.2026): Express is the third service product, one line per item line (devices and accessories).
 *   Round 9 (Simon via Argus, 11.10.2026): Express is counted per unit like Wunschort / Aufbau.
 *   The cart attribute `_lieferart` of rounds 1–4 is no longer written.
 *   data-fix (mode 'fix'): strips an invalid `_service`, adds missing service lines, sets service lines to their target
 *   quantity (item removed or changed, not Germany …) and drops a stale `_lieferart`, at most a few times in a row so a
 *   failing update cannot loop.
 *   <st-cart-delivery-sync> (round 4) runs the same fix on the /cart page, see below.
 * Round 7 (real-cart test, 11.10.2026): line keys change after cart changes, so every step reads /cart.js and finds
 *   the lines by variant and properties; a choice changes the device line first, then adds the service line.
 * Round 10 (Argus for Ali, 11.10.2026): the choice is per unit. On a line with quantity > 1 one unit is moved into its own
 *   line with the new properties (splitUnit); equal lines merge again. Cart counts leave service lines out.
 *   Replaced 18.10.2026 (QA preview round, Argus): the choice applies to ALL units of the line. No split any more; the
 *   whole device line gets or loses `_service`, and its service lines follow with the line quantity.
 * Re-render through the theme's cartUpdate pub/sub (like st-cart-discount); the open row, the drawer's scroll position and
 * the focus on the chosen option survive it.
 * Round 14 (Ali via Argus, 12.10.2026; replaces "no checkout lock" of round 2): while a choice or a fix is saving, Checkout,
 *   Apple Pay and PayPal in the drawer are locked (leaving the page aborted the save, so the checkout had no service line).
 *   Checkout first renders the drawer section once more and runs its 'fix' if a service line is missing. The wallet
 *   buttons need the click as a user gesture and cannot wait for that check; the lock is their protection.
 * Round 20 (Wrike 16.10.2026, nested lines tested on live): the service line is a nested line under its device
 *   (/cart/add.js parent_line_key), one per device line, still with `_service_for`; the checkout shows it under the device
 *   and removing the device removes it. Native bundles keep the flat line per SKU (Shopify refuses children under a
 *   bundle). Shopify does not scale a child with its parent, so the choice and mode 'fix' set its quantity. The device is
 *   changed first, then its children are synced (syncChildren), so a child lost on a remove/re-add is added again.
 */
if (!customElements.get("st-cart-delivery")) {
  // Position of the row to keep open across our own re-render (the line key changes with `_service`; the next drawer
  // opening starts collapsed again).
  let keepOpen = -1;
  let fixes = 0;
  let fixesReset;
  // Round 14: number of cart writes in flight. The drawer markup is replaced on every re-render, so lockCheckout() runs
  // again whenever a row connects.
  let saving = 0;

  const lockCheckout = () => {
    const locked = saving > 0;
    document
      .querySelectorAll("cart-drawer form.drawer__footer-buttons")
      .forEach((form) => {
        form.setAttribute("aria-busy", String(locked));
        form.querySelectorAll('button[name="checkout"]').forEach((button) => {
          if (locked) {
            button.disabled = true;
            button.dataset.stLocked = "";
          } else if ("stLocked" in button.dataset) {
            button.disabled = false;
            delete button.dataset.stLocked;
          }
        });
        form
          .querySelectorAll("st-wallet-row")
          .forEach((row) => (row.inert = locked));
      });
  };

  const whileSaving = (promise) => {
    saving += 1;
    lockCheckout();
    return promise.finally(() => {
      saving -= 1;
      lockCheckout();
    });
  };

  const post = (url, body, signal) =>
    fetch(url, {
      ...theme.utils.fetchConfig("json"),
      body: JSON.stringify(body),
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error(`cart ${response.status}`);
      return response.json();
    });

  const getCart = (signal) =>
    fetch(`${theme.routes.cart_url}.js`, { signal }).then((response) => {
      if (!response.ok) throw new Error(`cart ${response.status}`);
      return response.json();
    });

  // Round 7 (real-cart test on the preview, 11.10.2026): line keys can change after ANY cart change (adding a discounted
  // line gave the device line a new key, and /cart/change.js with the rendered key answered 400). So keys are never
  // reused after a change: a line is found again by variant and properties.
  const signature = (variantId, properties) =>
    `${variantId}|${JSON.stringify(
      Object.keys(properties || {})
        .sort()
        .map((key) => [key, properties[key]]),
    )}`;
  // Round 20: key of the parent line of a nested line (service under its device), undefined for top-level lines.
  const parentKey = (item) => item.parent_relationship?.parent_key;
  // Top-level lines only: a nested service line can share variant and properties with one under another device.
  const findLine = (cart, variantId, properties) =>
    cart.items.find(
      (item) =>
        !parentKey(item) &&
        signature(item.variant_id, item.properties) ===
          signature(variantId, properties),
    );
  // Current key of the line that had `key` in `before`; undefined when it is gone. A nested line is found by its own
  // signature under a parent with the same variant (the parent's properties may have changed in between).
  const currentKey = (key, before, cart) => {
    const item = before.items.find((line) => line.key === key);
    if (!item) return undefined;
    if (!parentKey(item))
      return findLine(cart, item.variant_id, item.properties)?.key;
    const parent = before.items.find((line) => line.key === parentKey(item));
    const own = signature(item.variant_id, item.properties);
    const matches = cart.items.filter(
      (line) =>
        parentKey(line) &&
        signature(line.variant_id, line.properties) === own &&
        cart.items.find((other) => other.key === parentKey(line))
          ?.variant_id === parent?.variant_id,
    );
    const exact = matches.find((line) => {
      const current = cart.items.find((other) => other.key === parentKey(line));
      return (
        signature(current.variant_id, current.properties) ===
        signature(parent?.variant_id, parent?.properties)
      );
    });
    return (exact || (matches.length === 1 ? matches[0] : undefined))?.key;
  };

  // Replaces the properties of a cart line (line object from /cart.js); resolves with the cart.
  // /cart/change.js ignores an empty `properties` object (preview test 11.10.2026: `_service` stayed). So when nothing
  // is left, the line is added again without properties and the old line is removed. The plain line merges with an
  // existing plain line of the same variant, as Simon's contract expects.
  const setProperties = async (line, properties, signal) => {
    if (Object.keys(properties).length)
      return post(
        theme.routes.cart_change_url,
        { id: line.key, quantity: line.quantity, properties },
        signal,
      );
    if (!Object.keys(line.properties || {}).length) return getCart(signal);
    await post(
      theme.routes.cart_add_url,
      { items: [{ id: line.variant_id, quantity: line.quantity }] },
      signal,
    );
    const cart = await getCart(signal);
    const old = findLine(cart, line.variant_id, line.properties);
    const plain = findLine(cart, line.variant_id, {});
    try {
      if (!old) throw new Error("cart line gone");
      return await post(
        theme.routes.cart_update_url,
        { updates: { [old.key]: 0 } },
        signal,
      );
    } catch (error) {
      // Take the added units back so the device is not in the cart twice.
      if (plain)
        await post(theme.routes.cart_update_url, {
          updates: {
            [plain.key]: Math.max(plain.quantity - line.quantity, 0),
          },
        }).catch(() => {});
      throw error;
    }
  };

  // Round 10 (Argus, 11.10.2026): "Artikel (n)" and the cart badges count products only. Service lines carry
  // `_service_for`. Runs after the theme's <cart-count> elements have set the plain item_count.
  const productCount = (cart) =>
    cart.item_count -
    cart.items
      .filter((item) => item.properties?._service_for)
      .reduce((sum, item) => sum + item.quantity, 0);
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (!event.cart?.items || event.cart.errors) return;
    const count = productCount(event.cart);
    queueMicrotask(() =>
      document.querySelectorAll("cart-count").forEach((element) => {
        element.innerText = count;
        element.hidden = count === 0;
      }),
    );
  });

  // Mode 'fix': strip `_service` (one change per line), add missing service lines, then set the quantities
  // with the given sections. The keys in `fix` are those of the rendered cart and are mapped to the current ones.
  const applyFix = async (fix, sections, signal) => {
    const before = await getCart(signal);
    let cart = before;
    for (const line of fix.strip || []) {
      const id = currentKey(line.id, before, cart);
      const item = id && cart.items.find((entry) => entry.key === id);
      if (!item) continue;
      const properties = { ...item.properties };
      delete properties._service;
      cart = await setProperties(item, properties, signal);
    }
    // Round 20: a nested line's parent_line_key is the rendered device key; skip it when the device is gone.
    const add = (fix.add || []).flatMap((item) => {
      if (!item.parent_line_key) return [item];
      const parent = currentKey(item.parent_line_key, before, cart);
      return parent ? [{ ...item, parent_line_key: parent }] : [];
    });
    if (add.length) {
      await post(theme.routes.cart_add_url, { items: add }, signal);
      cart = await getCart(signal);
    }
    const updates = {};
    for (const [key, quantity] of Object.entries(fix.updates || {})) {
      const id = currentKey(key, before, cart);
      if (id) updates[id] = quantity;
    }
    // ST 2026-10-30 (Wrike 4595016691): sections_url renders the sections in the page's language (also without a Referer).
    const body = { updates, sections, sections_url: window.location.pathname };
    if (fix.attributes) body.attributes = fix.attributes;
    return post(theme.routes.cart_update_url, body, signal);
  };

  // The 'fix' of a freshly rendered drawer section (root URL, so the drawer is not skipped on /cart); null when the cart
  // is in order.
  const pendingFix = (sectionId, signal) =>
    fetch(
      `${theme.routes.root_url.replace(/\/$/, "")}/?section_id=${encodeURIComponent(sectionId)}`,
      { signal },
    )
      .then((response) => {
        if (!response.ok) throw new Error(`section ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const raw = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector("st-cart-delivery[data-fix]")?.dataset.fix;
        return raw ? JSON.parse(raw) : null;
      });

  // Round 14: the drawer's Checkout button. Blocked while saving; otherwise the cart is checked (and repaired) first. A
  // failed or slow check (5 s) does not block the purchase: the checkout opens anyway.
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (
        !form.matches?.("cart-drawer form.drawer__footer-buttons") ||
        event.submitter?.name !== "checkout"
      )
        return;
      event.preventDefault();
      if (saving > 0) return;
      const sectionId = form
        .closest("cart-drawer")
        ?.querySelector('[id^="MiniCart-"]')
        ?.id.replace("MiniCart-", "");
      if (!sectionId) return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      whileSaving(
        pendingFix(sectionId, controller.signal).then(
          (fix) => fix && applyFix(fix, [], controller.signal),
        ),
      )
        .catch(() => {})
        .finally(() => {
          clearTimeout(timeout);
          // submit() skips the submit event (no loop) and leaves out the button, so the checkout flag goes in a hidden
          // field for this one submission.
          const target = form.isConnected
            ? form
            : document.querySelector("cart-drawer form.drawer__footer-buttons");
          if (!target) return;
          const field = document.createElement("input");
          field.type = "hidden";
          field.name = "checkout";
          target.append(field);
          HTMLFormElement.prototype.submit.call(target);
          field.remove();
        });
    },
    true,
  );

  // Round 12 (Ali's variant V1, 12.10.2026): "Voraussichtl. Lieferdatum" window = today in Germany plus the option's working
  // days ("1-4" → 1st to 4th working day after today), Mon–Fri without the national holidays. Format „04.11.–10.11.2026":
  // en dash without spaces, the year only at the end. Rows with a filled custom.lieferdatum carry no data-days-*.
  const holidays = (year) => {
    // Easter Sunday (Gregorian, anonymous algorithm), then the movable holidays relative to it.
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const h =
      (19 * a + b - Math.floor(b / 4) - Math.floor((8 * b + 13) / 25) + 15) %
      30;
    const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    const easter = Date.UTC(year, month - 1, day);
    const dayMs = 86400000;
    return new Set([
      Date.UTC(year, 0, 1),
      easter - 2 * dayMs,
      easter + dayMs,
      Date.UTC(year, 4, 1),
      easter + 39 * dayMs,
      easter + 50 * dayMs,
      Date.UTC(year, 9, 3),
      Date.UTC(year, 11, 25),
      Date.UTC(year, 11, 26),
    ]);
  };
  const isWorkingDay = (date) =>
    date.getUTCDay() % 6 !== 0 &&
    !holidays(date.getUTCFullYear()).has(date.getTime());
  const todayInGermany = () => {
    const [year, month, day] = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Berlin",
    })
      .format(new Date())
      .split("-")
      .map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };
  const addWorkingDays = (start, days) => {
    const date = new Date(start);
    for (let left = days; left > 0;) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (isWorkingDay(date)) left -= 1;
    }
    return date;
  };
  const pad = (number) => String(number).padStart(2, "0");
  const dayMonth = (date) =>
    `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.`;
  const deliveryWindow = (days, today = todayInGermany()) => {
    const match = /^\s*(\d{1,2})\s*[-–]\s*(\d{1,2})\s*$/.exec(days || "");
    if (!match) return "";
    const from = addWorkingDays(today, Number(match[1]));
    const to = addWorkingDays(
      today,
      Math.max(Number(match[1]), Number(match[2])),
    );
    return `${dayMonth(from)}–${dayMonth(to)}${to.getUTCFullYear()}`;
  };

  customElements.define(
    "st-cart-delivery",
    class StCartDelivery extends HTMLElement {
      connectedCallback() {
        this.box = this.querySelector("details.st-delivery-choice__box");
        this.error = this.querySelector(".st-delivery-choice__error");
        this.saved = this.querySelector(
          "input.st-delivery-choice__input:checked",
        );
        this.onChange = this.onChange.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onRemove = this.onRemove.bind(this);
        this.addEventListener("change", this.onChange);
        this.addEventListener("click", this.onRemove);
        if (this.box) {
          const open = keepOpen >= 0 && keepOpen === this.position();
          // Restored after a choice: no reveal (the scroll position is restored instead).
          this.restoring = open;
          this.box.open = open;
          this.box.addEventListener("toggle", this.onToggle);
        }
        this.showDate(this.saved?.value);
        lockCheckout();

        if (this.dataset.fix) this.fix();
      }

      // Round 12: computed delivery window for `option` (standard | wunschort | aufbau | express). Standard (and a paid
      // option without its own working days) shows the shop-wide <st-delivery-window> rendered by Liquid.
      showDate(option = "standard") {
        const line = this.querySelector(
          ".st-delivery-choice__date[data-computed]",
        );
        if (!line) return;
        const text =
          option === "standard"
            ? ""
            : deliveryWindow(
                line.dataset[
                  `days${option[0].toUpperCase()}${option.slice(1)}`
                ],
              );
        const standard = line.querySelector(
          ".st-delivery-choice__date-standard",
        );
        standard.hidden = Boolean(text);
        line.querySelector(".st-delivery-choice__date-value").textContent =
          text;
        line.hidden = !text && !standard.textContent.trim();
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onChange);
        this.removeEventListener("click", this.onRemove);
        this.box?.removeEventListener("toggle", this.onToggle);
        this.controller?.abort();
      }

      fix() {
        if (fixes >= 3) return;
        fixes += 1;
        clearTimeout(fixesReset);
        fixesReset = setTimeout(() => (fixes = 0), 10000);
        let body;
        try {
          body = JSON.parse(this.dataset.fix);
        } catch (error) {
          return;
        }
        this.removeAttribute("data-fix");
        whileSaving(this.update(body)).catch(() => {});
      }

      position() {
        const rows = this.closest("cart-items")?.querySelectorAll(
          "st-cart-delivery[data-line]",
        );
        return rows ? [...rows].indexOf(this) : -1;
      }

      onToggle() {
        if (this.restoring) {
          this.restoring = false;
          return;
        }
        if (this.isConnected && this.box.open) this.reveal();
      }

      // Wrike 4586442589: "Entfernen" in the PDP test variant = choose Standard (same path as the radio).
      onRemove(event) {
        if (!event.target.closest("[data-st-svc-remove]")) return;
        const standard = this.querySelector(
          'input.st-delivery-choice__input[value="standard"]',
        );
        if (!standard || standard.checked) return;
        standard.checked = true;
        standard.dispatchEvent(new Event("change", { bubbles: true }));
      }

      onChange(event) {
        const input = event.target.closest("input.st-delivery-choice__input");
        if (!input) return;
        event.stopPropagation();
        this.setError("");
        this.showDate(input.value);

        whileSaving(this.saveService(input)).catch((error) => {
          if (error.name === "AbortError") return;
          // Show the option that is still saved on the cart.
          if (this.saved) this.saved.checked = true;
          this.showDate(this.saved?.value);
          this.setError(this.getAttribute("data-error"));
        });
      }

      async saveService(input) {
        this.controller?.abort();
        const controller = new AbortController();
        this.controller = controller;
        const { signal } = controller;
        this.setAttribute("aria-busy", "true");
        const units = Number(input.dataset.units) || 1;
        const updates = {};
        try {
          // Fresh cart: the rendered keys are only used to find the lines in it.
          const before = await getCart(signal);
          const device = before.items.find(
            (item) => item.key === this.dataset.line,
          );
          if (!device) throw new Error("cart line gone");
          // /cart/change.js replaces all properties: keep the others.
          const saved = { ...device.properties };
          const properties = { ...saved };
          if (input.dataset.code) properties._service = input.dataset.code;
          else delete properties._service;

          // Device line first, then the service line. If the service fails, the device gets its old properties back.
          // QA preview round 18.10.2026: the whole line changes, also with quantity > 1 (no split).
          const changed = await setProperties(device, properties, signal);
          const nested = "nested" in this.dataset;
          const missing = nested
            ? this.syncChildren(changed, device.variant_id).missing
            : [];
          if (!nested && input.dataset.variant)
            missing.push({
              id: Number(input.dataset.variant),
              quantity: units,
              properties: { _service_for: this.dataset.sku },
            });
          if (missing.length) {
            try {
              await post(theme.routes.cart_add_url, { items: missing }, signal);
            } catch (error) {
              // Only when the line was not merged with another line of the same device and service.
              const moved = findLine(changed, device.variant_id, properties);
              if (moved?.quantity === device.quantity)
                await setProperties(moved, saved).catch(() => {});
              throw error;
            }
          }

          // Round 20: nested lines follow their device (old service 0, new one = device units).
          // Flat (bundle): the previous service line may also serve other lines of the same SKU: lower it by this line's
          // units.
          const old =
            !nested &&
            before.items.find((item) => item.key === this.dataset.serviceLine);
          if (nested)
            Object.assign(
              updates,
              this.syncChildren(await getCart(signal), device.variant_id)
                .updates,
            );
          else if (old) {
            const id = currentKey(old.key, before, await getCart(signal));
            if (id)
              updates[id] = Math.max(
                old.quantity - Number(this.dataset.serviceUnits),
                0,
              );
          }
        } finally {
          if (this.controller === controller)
            this.setAttribute("aria-busy", "false");
        }
        // Mode 'fix' in the re-rendered drawer corrects anything left over.
        return this.update({ updates }, true, true);
      }

      // Round 20 (nested service lines): for every top-level line of this device, its child with the service variant of
      // its `_service` gets the line's quantity (Shopify does not scale children), every other service child 0. Returns
      // the quantity updates and the children to add (parent_line_key = current device key).
      syncChildren(cart, variantId) {
        const variants = {};
        this.querySelectorAll(
          "input.st-delivery-choice__input[data-code]",
        ).forEach(
          (input) =>
            (variants[input.dataset.code] = Number(input.dataset.variant)),
        );
        const serviceIds = Object.values(variants);
        const updates = {};
        const missing = [];
        cart.items
          .filter((line) => line.variant_id === variantId && !parentKey(line))
          .forEach((line) => {
            const wanted = variants[line.properties?._service];
            let found = false;
            cart.items
              .filter(
                (child) =>
                  parentKey(child) === line.key &&
                  serviceIds.includes(child.variant_id),
              )
              .forEach((child) => {
                const target =
                  !found && child.variant_id === wanted ? line.quantity : 0;
                if (target) found = true;
                if (child.quantity !== target) updates[child.key] = target;
              });
            if (wanted && !found)
              missing.push({
                id: wanted,
                quantity: line.quantity,
                parent_line_key: line.key,
                properties: { _service_for: this.dataset.sku },
              });
          });
        return { updates, missing };
      }

      // Fix (or plain /cart/update.js) plus the drawer section, then re-render through cartUpdate.
      update(body, focus = false, keep = true) {
        this.controller?.abort();
        const controller = new AbortController();
        this.controller = controller;
        this.setAttribute("aria-busy", "true");
        const sectionId = this.dataset.sectionId;
        const position = this.position();

        return applyFix(body, [sectionId], controller.signal)
          .then((cart) => {
            const scrollTop = this.closest(".st-drawer-flow")?.scrollTop ?? 0;
            keepOpen = keep && this.box?.open ? position : -1;
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: "cart-delivery",
              cart,
            });
            keepOpen = -1;
            // The drawer markup was replaced synchronously: restore the scroll position, keep the focus on the choice.
            const miniCart = document.getElementById(`MiniCart-${sectionId}`);
            const flow = miniCart?.querySelector(".st-drawer-flow");
            if (flow) flow.scrollTop = scrollTop;
            if (focus && position >= 0) {
              const row = miniCart?.querySelectorAll(
                "cart-items st-cart-delivery[data-line]",
              )[position];
              row
                ?.querySelector("input.st-delivery-choice__input:checked")
                ?.focus({ preventScroll: true });
            }
            return cart;
          })
          .finally(() => {
            if (this.controller === controller)
              this.setAttribute("aria-busy", "false");
          });
      }

      // Scrolls the drawer so the whole row sits above the sticky zone (only while the zone is sticky).
      reveal() {
        const flow = this.closest(".st-drawer-flow");
        const fixed = flow?.querySelector(".st-drawer-fixed");
        if (!fixed || getComputedStyle(fixed).position !== "sticky") return;
        const overlap =
          this.getBoundingClientRect().bottom +
          8 -
          fixed.getBoundingClientRect().top;
        const room =
          this.getBoundingClientRect().top - flow.getBoundingClientRect().top;
        if (overlap > 0) flow.scrollTop += Math.min(overlap, Math.max(room, 0));
      }

      setError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = message === "";
      }
    },
  );

  // Round 4 (Argus 11.10.2026): the /cart page has no drawer, so mode 'fix' never ran there and a service line stayed
  // when its device was removed. On /cart this element renders the drawer section off-page (Section Rendering API, root
  // URL so the drawer is not skipped), runs its 'fix' update with the page's own cart sections and re-renders them.
  customElements.define(
    "st-cart-delivery-sync",
    class StCartDeliverySync extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event.source !== "cart-delivery" && !event.cart?.errors)
              this.sync();
          },
        );
        this.sync();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.controller?.abort();
      }

      sync() {
        if (fixes >= 3) return;
        this.controller?.abort();
        const controller = new AbortController();
        this.controller = controller;

        pendingFix(this.dataset.sectionId, controller.signal)
          .then((fix) => {
            if (!fix) return null;
            fixes += 1;
            clearTimeout(fixesReset);
            fixesReset = setTimeout(() => (fixes = 0), 10000);
            const sections = [];
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:bundled-sections", {
                bubbles: true,
                detail: { sections },
              }),
            );
            return applyFix(fix, sections, controller.signal).then((cart) =>
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                source: "cart-delivery",
                cart,
              }),
            );
          })
          .catch(() => {});
      }
    },
  );
}
